import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Pencil, Trash2, Undo2, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import {
  useCancelLeaveRequest,
  useDecideLeaveRequest,
  useDeleteLeaveRequest,
} from '../../hooks/useLeave';
import type { LeaveRequest } from '../../lib/leaveApi';
import { formatLeaveLength, formatLeaveRange } from '../../lib/leaveConstants';
import { useConfirm, usePrompt } from '../ui/ConfirmProvider';
import { useToast } from '../tasks/linear/Toast';
import { LeaveStatusBadge } from './LeaveStatusBadge';
import { LeaveTypeBadge } from './LeaveTypeBadge';

type SortField = 'person' | 'type' | 'startDate' | 'status' | 'createdAt';

function SortHeader({
  label,
  field,
  sort,
  order,
  onSort,
}: {
  label: string;
  field: SortField;
  sort: SortField;
  order: 'asc' | 'desc';
  onSort: (field: SortField) => void;
}) {
  const active = sort === field;
  return (
    <th className="px-3 py-2 text-left font-medium text-gray-600">
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 whitespace-nowrap hover:text-foreground"
      >
        {label}
        {active && <span aria-hidden>{order === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

/**
 * The leave table, shared by "Cererile mele" and "Toate cererile" — the only
 * difference is whether the person column is drawn.
 *
 * Which buttons a row gets is read straight off `request.permissions`, which
 * the server fills in per row (own vs. someone else's, pending vs. decided,
 * approver vs. manager). Re-deriving those rules in the browser is how the two
 * sides drift apart: a button that 403s on click, or a manager who cannot see
 * an action they actually have.
 *
 * Sorting is local. The list endpoint takes filters and a page but no sort, and
 * a leave list is small enough (one team, one year) that sorting the page in
 * the browser is both correct and instant.
 */
export function LeaveRequestsTable({
  requests,
  showPerson = false,
  filtered = false,
  emptyLabel,
  selectedId,
  onSelect,
  onEdit,
}: {
  requests: LeaveRequest[];
  showPerson?: boolean;
  /** Filters are active, so the empty state says "nothing matches" instead. */
  filtered?: boolean;
  emptyLabel?: string;
  /** Deep-linked / clicked row: highlighted and scrolled into view. */
  selectedId?: string | null;
  onSelect?: (request: LeaveRequest) => void;
  /**
   * Opens the edit form. Omit it and the pencil is not drawn even on a row the
   * caller may edit — a button that leads nowhere is worse than no button.
   */
  onEdit?: (request: LeaveRequest) => void;
}) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const decide = useDecideLeaveRequest();
  const cancel = useCancelLeaveRequest();
  const remove = useDeleteLeaveRequest();

  const [sort, setSort] = useState<SortField>('startDate');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  // A request reached from an email or a push notification has to be visible
  // without the person hunting for it, even when it is far down the list.
  useEffect(() => {
    if (!selectedId) return;
    rowRefs.current[selectedId]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selectedId, requests]);

  function toggleSort(field: SortField) {
    if (field === sort) setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(field);
      setOrder('asc');
    }
  }

  const sorted = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1;
    const key = (r: LeaveRequest): string => {
      switch (sort) {
        case 'person':
          return r.user.name;
        case 'type':
          return r.type;
        case 'status':
          return r.status;
        case 'createdAt':
          return r.createdAt;
        default:
          return r.startDate;
      }
    };
    // Spread first: the array comes from react-query's cache and must not be
    // sorted in place.
    return [...requests].sort((a, b) => key(a).localeCompare(key(b)) * dir);
  }, [requests, sort, order]);

  /** One place to turn a failed mutation into a readable toast. */
  function fail(err: unknown) {
    toast.show(err instanceof Error ? err.message : t('common.error'));
  }

  async function onApprove(request: LeaveRequest) {
    try {
      await decide.mutateAsync({ id: request.id, decision: 'approve' });
      toast.show(t('leave.status.APPROVED'));
    } catch (err) {
      fail(err);
    }
  }

  async function onReject(request: LeaveRequest) {
    // The note is optional, so an empty answer still rejects; only Escape
    // (null) aborts.
    const note = await prompt({
      title: t('leave.reject'),
      message: t('leave.rejectPrompt'),
      confirmLabel: t('leave.reject'),
    });
    if (note === null) return;
    try {
      await decide.mutateAsync({ id: request.id, decision: 'reject', note: note || null });
      toast.show(t('leave.status.REJECTED'));
    } catch (err) {
      fail(err);
    }
  }

  async function onWithdraw(request: LeaveRequest) {
    if (
      !(await confirm({
        message: t('leave.confirmWithdraw'),
        confirmLabel: t('leave.withdraw'),
        danger: true,
      }))
    ) {
      return;
    }
    try {
      await cancel.mutateAsync(request.id);
      toast.show(t('leave.status.CANCELLED'));
    } catch (err) {
      fail(err);
    }
  }

  async function onDelete(request: LeaveRequest) {
    if (
      !(await confirm({
        message: t('leave.confirmDelete'),
        confirmLabel: t('leave.delete'),
        danger: true,
      }))
    ) {
      return;
    }
    try {
      // No toast: the row disappearing is the confirmation.
      await remove.mutateAsync(request.id);
    } catch (err) {
      fail(err);
    }
  }

  if (sorted.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {filtered ? t('leave.emptyFiltered') : (emptyLabel ?? t('leave.empty'))}
      </p>
    );
  }

  const year = new Date().getFullYear();
  const filedFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'short' });

  return (
    // A leave row carries eight columns; on a phone the table scrolls
    // sideways rather than wrapping into an unreadable stack.
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-accent/40">
          <tr>
            {showPerson && (
              <SortHeader
                label={t('leave.column.person')}
                field="person"
                sort={sort}
                order={order}
                onSort={toggleSort}
              />
            )}
            <SortHeader
              label={t('leave.column.type')}
              field="type"
              sort={sort}
              order={order}
              onSort={toggleSort}
            />
            <SortHeader
              label={t('leave.column.period')}
              field="startDate"
              sort={sort}
              order={order}
              onSort={toggleSort}
            />
            <th className="px-3 py-2 text-left font-medium text-gray-600">
              {t('leave.column.length')}
            </th>
            <SortHeader
              label={t('leave.column.status')}
              field="status"
              sort={sort}
              order={order}
              onSort={toggleSort}
            />
            <SortHeader
              label={t('leave.column.filed')}
              field="createdAt"
              sort={sort}
              order={order}
              onSort={toggleSort}
            />
            <th className="px-3 py-2 text-left font-medium text-gray-600">
              {t('leave.column.decision')}
            </th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-card">
          {sorted.map((request) => {
            const p = request.permissions;
            const canEditHere = p.canEdit && Boolean(onEdit);
            const hasActions = p.canDecide || canEditHere || p.canCancel || p.canDelete;
            return (
              <tr
                key={request.id}
                ref={(el) => {
                  rowRefs.current[request.id] = el;
                }}
                onClick={() => onSelect?.(request)}
                className={`${onSelect ? 'cursor-pointer' : ''} ${
                  request.id === selectedId ? 'bg-accent/60' : 'hover:bg-accent/30'
                }`}
              >
                {showPerson && (
                  <td className="whitespace-nowrap px-3 py-2 text-foreground">
                    {request.user.name}
                  </td>
                )}
                <td className="px-3 py-2">
                  <LeaveTypeBadge type={request.type} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-foreground">
                  {formatLeaveRange(request.startDate, request.endDate, locale, year)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {formatLeaveLength(request.type, request.workingDays, request.hours)}
                </td>
                <td className="px-3 py-2">
                  <LeaveStatusBadge status={request.status} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {filedFormat.format(new Date(request.createdAt))}
                </td>
                <td className="max-w-xs px-3 py-2 text-xs text-muted-foreground">
                  {request.decidedBy ? (
                    <>
                      <div>{t('leave.decidedBy', { name: request.decidedBy.name })}</div>
                      {request.decisionNote && <div className="italic">{request.decisionNote}</div>}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-2">
                  {hasActions && (
                    // The row itself is clickable, so every action stops the
                    // click from also selecting/opening the request.
                    <div
                      className="flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {p.canDecide && (
                        <>
                          <button
                            type="button"
                            onClick={() => void onApprove(request)}
                            disabled={decide.isPending}
                            title={t('leave.approve')}
                            className="ui-btn-primary px-2 py-1 text-xs"
                          >
                            <Check size={13} />
                            <span className="hidden sm:inline">{t('leave.approve')}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => void onReject(request)}
                            disabled={decide.isPending}
                            title={t('leave.reject')}
                            className="ui-btn-danger px-2 py-1 text-xs"
                          >
                            <X size={13} />
                            <span className="hidden sm:inline">{t('leave.reject')}</span>
                          </button>
                        </>
                      )}
                      {canEditHere && (
                        <button
                          type="button"
                          onClick={() => onEdit?.(request)}
                          title={t('leave.edit')}
                          aria-label={t('leave.edit')}
                          className="ui-btn-secondary px-2 py-1 text-xs"
                        >
                          <Pencil size={13} />
                        </button>
                      )}
                      {p.canCancel && (
                        <button
                          type="button"
                          onClick={() => void onWithdraw(request)}
                          disabled={cancel.isPending}
                          title={t('leave.withdraw')}
                          aria-label={t('leave.withdraw')}
                          className="ui-btn-secondary px-2 py-1 text-xs"
                        >
                          <Undo2 size={13} />
                        </button>
                      )}
                      {p.canDelete && (
                        <button
                          type="button"
                          onClick={() => void onDelete(request)}
                          disabled={remove.isPending}
                          title={t('leave.delete')}
                          aria-label={t('leave.delete')}
                          className="ui-btn-danger px-2 py-1 text-xs"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
